import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ApiError,
  getAdminEvents,
  getAdminTeam,
  type AdminEvent,
  type AdminEventsFilter,
  type AdminTeamDetail,
} from "../api.js";
import { AppShell } from "../components/AppShell.js";
import { ErrorNote, Spinner } from "../components/ui.js";
import { AdminNotFound, AdminTag, Chip } from "./AdminChrome.js";
import { AdminTimeline } from "./AdminTimeline.js";
import { seen, usd } from "./format.js";

/** The API's page of events; a full page means there may be older ones. */
const EVENTS_PAGE = 100;

const TYPES: [AdminEventsFilter, string][] = [
  ["all", "Everything"],
  ["attempts", "Attempts"],
  ["failures", "Failures"],
  ["projects", "Projects"],
  ["money", "Money"],
];

const longDate = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

const errorText = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

const Stat: React.FC<{ label: string; value: React.ReactNode; note?: string }> = ({ label, value, note }) => (
  <div className="flex flex-col gap-1 rounded-[10px] border border-[var(--edge)] bg-bay-850 px-3.5 py-3">
    <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-ink-400">{label}</span>
    <b className="text-[19px] font-semibold tabular-nums text-ink-100">{value}</b>
    {note ? <small className="text-[11.5px] text-ink-300">{note}</small> : null}
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col gap-2 rounded-[10px] border border-[var(--edge)] bg-bay-850 px-3.5 py-3">
    <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-400">{title}</h2>
    {children}
  </section>
);

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-2 text-[12.5px]">
    <span className="min-w-0 break-words text-ink-100">{label}</span>
    <span className="shrink-0 text-right tabular-nums text-ink-300">{value}</span>
  </div>
);

/** One team: its totals, its people, and everything it did, newest first. */
export const AdminTeam: React.FC = () => {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const [team, setTeam] = useState<AdminTeamDetail | null>(null);
  const [type, setType] = useState<AdminEventsFilter>("all");
  const [events, setEvents] = useState<AdminEvent[] | null>(null);
  const [more, setMore] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Apart, so refetching the events for another chip can't wipe out why the
  // team itself didn't load (which would leave its spinner up for good).
  const [teamError, setTeamError] = useState<string | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  /**
   * Which events query is current. Bumped whenever orgId or type changes; a
   * "Load older" in flight then drops its answer and its loading state rather
   * than appending the old filter's events to the new list.
   */
  const generation = useRef(0);

  const failWith = (set: (message: string) => void) => (caught: unknown) => {
    if (caught instanceof ApiError && caught.status === 404) setNotFound(true);
    else set(errorText(caught));
  };

  useEffect(() => {
    let live = true;
    setTeam(null);
    setTeamError(null);
    setNotFound(false);
    getAdminTeam(orgId)
      .then((detail) => {
        if (live) setTeam(detail);
      })
      .catch((caught: unknown) => {
        if (live) failWith(setTeamError)(caught);
      });
    return () => {
      live = false;
    };
  }, [orgId]);

  useEffect(() => {
    const mine = ++generation.current;
    setEvents(null);
    setMore(false);
    setOpenId(null);
    setEventsError(null);
    setLoadingOlder(false);
    getAdminEvents(orgId, { type })
      .then((page) => {
        if (generation.current !== mine) return;
        setEvents(page.events);
        setMore(page.events.length === EVENTS_PAGE);
      })
      .catch((caught: unknown) => {
        if (generation.current === mine) failWith(setEventsError)(caught);
      });
  }, [orgId, type]);

  // On leaving the page, nothing still in flight may set state.
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );

  const loadOlder = async () => {
    const last = events?.at(-1);
    if (!last || loadingOlder) return;
    const mine = generation.current;
    setLoadingOlder(true);
    setEventsError(null);
    try {
      const page = await getAdminEvents(orgId, { type, before: last.at });
      if (generation.current !== mine) return;
      setEvents((current) => [...(current ?? []), ...page.events]);
      setMore(page.events.length === EVENTS_PAGE);
    } catch (caught) {
      if (generation.current === mine) failWith(setEventsError)(caught);
    } finally {
      // A newer query reset `loadingOlder` when it started.
      if (generation.current === mine) setLoadingOlder(false);
    }
  };

  if (notFound) return <AdminNotFound />;

  const title = team ? (team.name ?? team.members[0]?.email ?? "Team") : "…";
  const lastSeen = team?.members
    .map((member) => member.lastSeen)
    .sort()
    .at(-1);
  const people = team ? (team.members.length === 1 ? "1 person" : `${team.members.length} people`) : "";

  return (
    <AppShell
      wide
      breadcrumb={
        <>
          <Link to="/admin" className="text-ink-400 transition-colors hover:text-ink-200">
            Admin
          </Link>
          <span className="text-ink-400">/</span>
          <span className="truncate font-medium text-ink-100">{title}</span>
          <AdminTag />
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {teamError ? <ErrorNote>{teamError}</ErrorNote> : null}

        {team ? (
          <>
            <div className="flex flex-col gap-1">
              <h1 className="break-words text-[21px] font-semibold text-ink-100">{title}</h1>
              <p className="break-words text-[12.5px] text-ink-300">
                Team since {longDate.format(new Date(team.createdAt))} · {people} ·{" "}
                {team.members.map((member) => member.email).join(", ")}
                {lastSeen ? ` · last seen ${seen(lastSeen)}` : ""}
              </p>
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
              <Stat
                label="Balance"
                value={usd(team.totals.balanceUsd)}
                note={`paid ${usd(team.totals.paidUsd)}, spent ${usd(team.totals.spentUsd)}`}
              />
              <Stat
                label="Films"
                value={team.totals.films}
                note={team.totals.films === 0 ? "none finished" : undefined}
              />
              <Stat label="Tried" value={team.totals.attempts} note={`${team.totals.failures} failed`} />
              <Stat label="Our cost" value={usd(team.totals.costUsd)} />
            </div>
          </>
        ) : !teamError ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2" role="group" aria-label="Show">
          {TYPES.map(([value, label]) => (
            <Chip key={value} on={type === value} onClick={() => setType(value)}>
              {label}
            </Chip>
          ))}
        </div>

        <div className="grid grid-cols-1 items-start gap-4 min-[820px]:grid-cols-[minmax(0,1fr)_260px]">
          <div className="flex min-w-0 flex-col gap-3">
            {eventsError ? <ErrorNote>{eventsError}</ErrorNote> : null}
            {events ? (
              <AdminTimeline events={events} openId={openId} onOpen={setOpenId} now={new Date()} />
            ) : !eventsError ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : null}
            {events && more ? (
              <div className="flex justify-center">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={loadingOlder}
                  onClick={() => void loadOlder()}
                >
                  {loadingOlder ? "Loading…" : "Load older"}
                </button>
              </div>
            ) : null}
          </div>

          {team ? (
            <aside className="flex flex-col gap-3">
              <Card title="People">
                {team.members.map((member) => (
                  <Row key={member.email} label={member.email} value={seen(member.lastSeen)} />
                ))}
              </Card>
              <Card title="Account">
                <Row label="Free videos" value={`${team.freeVideos.used} of ${team.freeVideos.limit} used`} />
                <Row label="Free forever" value={team.freeForever ? "yes" : "no"} />
                <Row label="Brand kit" value={team.brandKitSet ? "set" : "not set"} />
                <p className="text-[12px] leading-normal text-ink-300">
                  Read only. Changing credit or flags stays in Stripe and PropelAuth for now.
                </p>
              </Card>
              <Card title="Kept">
                <p className="text-[12px] leading-normal text-ink-300">
                  Attempts are kept 90 days and deleted with the account. Projects, jobs and credit follow their own
                  rules.
                </p>
              </Card>
            </aside>
          ) : null}
        </div>
      </div>
    </AppShell>
  );
};
